/**
 * VH-296 — the browser's side of a passkey: ask the phone (fingerprint, face or
 * phone PIN) and hand its answer to the server as JSON.
 *
 * The server sends WebAuthn options as JSON with base64url strings; the
 * browser wants ArrayBuffers. The newer browsers convert themselves
 * (`PublicKeyCredential.parse…OptionsFromJSON`, `credential.toJSON()`); the
 * helpers below do the same by hand for the others. Nothing here keeps a
 * challenge or an answer: each is used once and dropped.
 */

/** Can this browser use passkeys at all? */
export function passkeysSupported() {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential === 'function'
    && Boolean(navigator?.credentials)
}

export function base64urlToBuffer(text) {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const descriptors = (list) => (list || []).map((item) => ({ ...item, id: base64urlToBuffer(item.id) }))

/** Options for `navigator.credentials.get()` from the server's JSON. */
export function requestOptionsFromJSON(json) {
  if (typeof window.PublicKeyCredential?.parseRequestOptionsFromJSON === 'function') {
    return window.PublicKeyCredential.parseRequestOptionsFromJSON(json)
  }
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    allowCredentials: descriptors(json.allowCredentials),
  }
}

/** Options for `navigator.credentials.create()` from the server's JSON. */
export function creationOptionsFromJSON(json) {
  if (typeof window.PublicKeyCredential?.parseCreationOptionsFromJSON === 'function') {
    return window.PublicKeyCredential.parseCreationOptionsFromJSON(json)
  }
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    user: { ...json.user, id: base64urlToBuffer(json.user.id) },
    excludeCredentials: descriptors(json.excludeCredentials),
  }
}

/** The phone's answer as the JSON the server reads. */
export function credentialToJSON(credential) {
  if (typeof credential?.toJSON === 'function') return credential.toJSON()
  const response = credential.response
  const json = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    response: { clientDataJSON: bufferToBase64url(response.clientDataJSON) },
  }
  if (response.attestationObject) {
    json.response.attestationObject = bufferToBase64url(response.attestationObject)
    json.response.transports = response.getTransports?.() || []
  } else {
    json.response.authenticatorData = bufferToBase64url(response.authenticatorData)
    json.response.signature = bufferToBase64url(response.signature)
    if (response.userHandle) json.response.userHandle = bufferToBase64url(response.userHandle)
  }
  return json
}

/** The member closed the phone's prompt or it timed out: not an error to show loudly. */
export const wasDismissed = (error) => error?.name === 'NotAllowedError' || error?.name === 'AbortError'

// What else `navigator.credentials` throws: the page is not on the passkey's
// site, the passkey exists already, the device cannot, or it failed itself.
const BROWSER_REFUSALS = ['SecurityError', 'InvalidStateError', 'NotSupportedError', 'UnknownError', 'ConstraintError']

/** The browser or the phone refused (not the server, not the network). */
export const wasRefusedByBrowser = (error) => !error?.response && BROWSER_REFUSALS.includes(error?.name)

/** Ask the phone to answer with an existing passkey. */
export async function answerWithPasskey(optionsJSON) {
  const credential = await navigator.credentials.get({ publicKey: requestOptionsFromJSON(optionsJSON) })
  return credentialToJSON(credential)
}

/** Ask the phone to make a new passkey. */
export async function makePasskey(optionsJSON) {
  const credential = await navigator.credentials.create({ publicKey: creationOptionsFromJSON(optionsJSON) })
  return credentialToJSON(credential)
}
